import React, { useState, useCallback } from 'react';
import { Download, FileText, Filter, RefreshCw, CheckCircle, AlertTriangle, DollarSign, BarChart3, Printer } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatAmount = (v, currency = 'XOF') => {
  if (!v && v !== 0) return '—';
  const opts = currency === 'XOF'
    ? { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }
    : { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return new Intl.NumberFormat('fr-FR', opts)?.format(v);
};

const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const formatDateTime = (d) => d ? new Date(d)?.toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const escapeCSV = (v) => {
  if (v == null) return '';
  const s = String(v);
  if (s?.includes(';') || s?.includes('"') || s?.includes('\n')) return `"${s?.replace(/"/g, '""')}"`;
  return s;
};

// ─── Fetch Settlement Data ────────────────────────────────────────────────────
const fetchSettlementData = async (filters) => {
  const { dateFrom, dateTo, recipientRole, status } = filters;

  const applyDate = (q) => {
    if (dateFrom) q = q?.gte('created_at', dateFrom);
    if (dateTo) q = q?.lte('created_at', dateTo + 'T23:59:59');
    return q;
  };

  const [escrowRes, commissionsRes, paymentsRes] = await Promise.allSettled([
    applyDate(
      supabase?.from('escrow_transactions')
        ?.select('id, title, item_title, amount_total, total_amount, status, paystack_reference, reference, seller_id, currency, created_at, updated_at, released_at')
        ?.order('created_at', { ascending: false })
        ?.limit(500)
    ),
    (() => {
      let q = supabase?.from('commission_payouts')
        ?.select('id, escrow_transaction_id, recipient_id, recipient_role, recipient_name, recipient_email, commission_rate, base_amount, commission_amount, currency, status, released_at, tax_withholding_amount, tax_country, tax_rate_applied, net_after_tax, payout_currency, payout_channel, converted_amount, net_payout_amount, fx_rate_applied, created_at')
        ?.order('created_at', { ascending: false })
        ?.limit(500);
      if (recipientRole !== 'all') q = q?.eq('recipient_role', recipientRole);
      if (status !== 'all') q = q?.eq('status', status);
      return applyDate(q);
    })(),
    applyDate(
      supabase?.from('payments')
        ?.select('id, amount, status, paystack_reference, session_id, created_at')
        ?.order('created_at', { ascending: false })
        ?.limit(500)
    ),
  ]);

  return {
    escrow: escrowRes?.value?.data || [],
    commissions: commissionsRes?.value?.data || [],
    payments: paymentsRes?.value?.data || [],
  };
};

// ─── CSV Builder ──────────────────────────────────────────────────────────────
const buildSettlementCSV = (data, filters) => {
  const rows = [];
  const BOM = '\uFEFF';

  // Section 1: Transactions
  rows?.push('=== LISTE DES TRANSACTIONS SÉQUESTRE ===');
  rows?.push(['Référence', 'Titre', 'Montant Total', 'Statut', 'Devise', 'Référence Paystack', 'Date création', 'Date libération']?.map(escapeCSV)?.join(';'));
  data?.escrow?.forEach(t => {
    rows?.push([
      t?.reference || t?.id?.slice(0, 8),
      t?.item_title || t?.title || '—',
      t?.amount_total || t?.total_amount || 0,
      t?.status,
      t?.currency || 'XOF',
      t?.paystack_reference || '—',
      formatDate(t?.created_at),
      formatDate(t?.released_at),
    ]?.map(escapeCSV)?.join(';'));
  });

  rows?.push('');
  rows?.push('=== COMMISSIONS ET PAIEMENTS AGENTS/NOTAIRES ===');
  rows?.push(['Destinataire', 'Rôle', 'Email', 'Taux Commission', 'Montant Base', 'Commission Brute', 'Retenue Fiscale', 'Taux Retenue', 'Pays Fiscal', 'Montant Net', 'Devise Paiement', 'Canal', 'Montant Converti', 'Taux FX', 'Statut', 'Date Paiement']?.map(escapeCSV)?.join(';'));
  data?.commissions?.forEach(c => {
    rows?.push([
      c?.recipient_name || '—',
      c?.recipient_role === 'agent' ? 'Agent immobilier' : c?.recipient_role === 'notary' ? 'Notaire' : 'Plateforme',
      c?.recipient_email || '—',
      `${c?.commission_rate || 0}%`,
      c?.base_amount || 0,
      c?.commission_amount || 0,
      c?.tax_withholding_amount || 0,
      c?.tax_rate_applied ? `${(c?.tax_rate_applied * 100)?.toFixed(1)}%` : '—',
      c?.tax_country || '—',
      c?.net_after_tax || c?.commission_amount || 0,
      c?.payout_currency || 'XOF',
      c?.payout_channel || '—',
      c?.converted_amount || '—',
      c?.fx_rate_applied || '—',
      c?.status === 'released' ? 'Payé' : c?.status === 'pending' ? 'En attente' : 'Annulé',
      formatDate(c?.released_at),
    ]?.map(escapeCSV)?.join(';'));
  });

  rows?.push('');
  rows?.push('=== RÉSUMÉ DES PAIEMENTS ===');
  rows?.push(['Référence Paystack', 'Montant', 'Statut', 'Date']?.map(escapeCSV)?.join(';'));
  data?.payments?.forEach(p => {
    rows?.push([
      p?.paystack_reference || p?.session_id || '—',
      p?.amount || 0,
      p?.status,
      formatDate(p?.created_at),
    ]?.map(escapeCSV)?.join(';'));
  });

  // Summary section
  const totalEscrow = data?.escrow?.reduce((s, t) => s + (t?.amount_total || t?.total_amount || 0), 0);
  const totalCommissions = data?.commissions?.reduce((s, c) => s + (c?.commission_amount || 0), 0);
  const totalTax = data?.commissions?.reduce((s, c) => s + (c?.tax_withholding_amount || 0), 0);
  const totalNet = data?.commissions?.reduce((s, c) => s + (c?.net_after_tax || c?.commission_amount || 0), 0);
  const totalPayments = data?.payments?.reduce((s, p) => s + (p?.amount || 0), 0);

  rows?.push('');
  rows?.push('=== RÉCAPITULATIF FINANCIER ===');
  rows?.push(['Indicateur', 'Valeur (XOF)']?.map(escapeCSV)?.join(';'));
  rows?.push([`Période`, `${filters?.dateFrom || 'Début'} → ${filters?.dateTo || 'Aujourd\'hui'}`]?.map(escapeCSV)?.join(';'));
  rows?.push([`Nombre de transactions séquestre`, data?.escrow?.length]?.map(escapeCSV)?.join(';'));
  rows?.push([`Montant total séquestre`, totalEscrow]?.map(escapeCSV)?.join(';'));
  rows?.push([`Total commissions brutes`, totalCommissions]?.map(escapeCSV)?.join(';'));
  rows?.push([`Total retenues fiscales`, totalTax]?.map(escapeCSV)?.join(';'));
  rows?.push([`Total paiements nets`, totalNet]?.map(escapeCSV)?.join(';'));
  rows?.push([`Total paiements reçus`, totalPayments]?.map(escapeCSV)?.join(';'));
  rows?.push([`Généré le`, formatDateTime(new Date()?.toISOString())]?.map(escapeCSV)?.join(';'));

  return BOM + rows?.join('\n');
};

// ─── Print HTML Report ────────────────────────────────────────────────────────
const buildPrintHTML = (data, filters, summary) => {
  return `
    <!DOCTYPE html>
    <html lang="fr">
    <head>
      <meta charset="UTF-8">
      <title>Rapport de Règlement — KapitalStates</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 11px; color: #333; margin: 20px; }
        h1 { font-size: 18px; color: #1a1a1a; border-bottom: 2px solid #D4AF37; padding-bottom: 8px; }
        h2 { font-size: 13px; color: #444; margin-top: 20px; margin-bottom: 8px; border-left: 3px solid #D4AF37; padding-left: 8px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 16px; font-size: 10px; }
        th { background: #f5f5f5; padding: 6px 8px; text-align: left; border: 1px solid #ddd; font-weight: bold; }
        td { padding: 5px 8px; border: 1px solid #eee; }
        tr:nth-child(even) { background: #fafafa; }
        .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 16px 0; }
        .summary-card { background: #f9f9f9; border: 1px solid #e0e0e0; border-radius: 6px; padding: 12px; }
        .summary-card .label { font-size: 10px; color: #666; margin-bottom: 4px; }
        .summary-card .value { font-size: 14px; font-weight: bold; color: #1a1a1a; }
        .badge { display: inline-block; padding: 2px 6px; border-radius: 10px; font-size: 9px; font-weight: bold; }
        .badge-green { background: #d1fae5; color: #065f46; }
        .badge-amber { background: #fef3c7; color: #92400e; }
        .badge-red { background: #fee2e2; color: #991b1b; }
        .footer { margin-top: 30px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 9px; color: #888; }
        @media print { body { margin: 10px; } }
      </style>
    </head>
    <body>
      <h1>📊 Rapport de Règlement — KapitalStates</h1>
      <p style="color:#666; font-size:10px;">
        Période: <strong>${filters?.dateFrom || 'Début'}</strong> → <strong>${filters?.dateTo || "Aujourd'hui"}</strong> &nbsp;|&nbsp;
        Généré le: <strong>${formatDateTime(new Date()?.toISOString())}</strong>
      </p>

      <div class="summary-grid">
        <div class="summary-card">
          <div class="label">Transactions séquestre</div>
          <div class="value">${data?.escrow?.length || 0}</div>
        </div>
        <div class="summary-card">
          <div class="label">Montant total séquestre</div>
          <div class="value">${formatAmount(summary?.totalEscrow)}</div>
        </div>
        <div class="summary-card">
          <div class="label">Commissions brutes</div>
          <div class="value">${formatAmount(summary?.totalCommissions)}</div>
        </div>
        <div class="summary-card">
          <div class="label">Retenues fiscales</div>
          <div class="value" style="color:#dc2626">${formatAmount(summary?.totalTax)}</div>
        </div>
        <div class="summary-card">
          <div class="label">Paiements nets</div>
          <div class="value" style="color:#059669">${formatAmount(summary?.totalNet)}</div>
        </div>
        <div class="summary-card">
          <div class="label">Paiements reçus</div>
          <div class="value">${formatAmount(summary?.totalPayments)}</div>
        </div>
      </div>

      <h2>Transactions Séquestre</h2>
      <table>
        <thead>
          <tr><th>Référence</th><th>Titre</th><th>Montant</th><th>Statut</th><th>Date</th><th>Libération</th></tr>
        </thead>
        <tbody>
          ${data?.escrow?.slice(0, 100)?.map(t => `
            <tr>
              <td>${t?.reference || t?.id?.slice(0, 8)}</td>
              <td>${t?.item_title || t?.title || '—'}</td>
              <td><strong>${formatAmount(t?.amount_total || t?.total_amount)}</strong></td>
              <td><span class="badge ${t?.status === 'released' ? 'badge-green' : t?.status === 'disputed' ? 'badge-red' : 'badge-amber'}">${t?.status}</span></td>
              <td>${formatDate(t?.created_at)}</td>
              <td>${formatDate(t?.released_at)}</td>
            </tr>
          `)?.join('')}
        </tbody>
      </table>

      <h2>Commissions & Paiements Agents/Notaires</h2>
      <table>
        <thead>
          <tr><th>Destinataire</th><th>Rôle</th><th>Commission</th><th>Retenue</th><th>Net</th><th>Devise</th><th>Canal</th><th>Statut</th></tr>
        </thead>
        <tbody>
          ${data?.commissions?.slice(0, 100)?.map(c => `
            <tr>
              <td>${c?.recipient_name || '—'}<br><span style="color:#888;font-size:9px">${c?.recipient_email || ''}</span></td>
              <td>${c?.recipient_role === 'agent' ? '🏠 Agent' : '⚖️ Notaire'}</td>
              <td>${formatAmount(c?.commission_amount)}</td>
              <td style="color:#dc2626">${c?.tax_withholding_amount ? formatAmount(c?.tax_withholding_amount) : '—'}</td>
              <td style="color:#059669"><strong>${formatAmount(c?.net_after_tax || c?.commission_amount)}</strong></td>
              <td>${c?.payout_currency || 'XOF'}</td>
              <td>${c?.payout_channel || '—'}</td>
              <td><span class="badge ${c?.status === 'released' ? 'badge-green' : 'badge-amber'}">${c?.status === 'released' ? 'Payé' : 'En attente'}</span></td>
            </tr>
          `)?.join('')}
        </tbody>
      </table>

      <div class="footer">
        <p>Ce rapport est généré automatiquement par KapitalStates. Pour usage interne — Agents immobiliers et Notaires agréés uniquement.</p>
        <p>KapitalStates — Abidjan, Côte d'Ivoire | kapitalstates.com</p>
      </div>
    </body>
    </html>
  `;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const SettlementReportGenerator = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [toast, setToast] = useState(null);
  const [filters, setFilters] = useState({
    dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
    dateTo: new Date()?.toISOString()?.split('T')?.[0],
    recipientRole: 'all',
    status: 'all',
  });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleGenerate = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSettlementData(filters);
      setReportData(data);
    } catch (e) {
      showToast('Erreur lors de la génération du rapport', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const computeSummary = (data) => {
    if (!data) return {};
    return {
      totalEscrow: data?.escrow?.reduce((s, t) => s + (t?.amount_total || t?.total_amount || 0), 0),
      totalCommissions: data?.commissions?.reduce((s, c) => s + (c?.commission_amount || 0), 0),
      totalTax: data?.commissions?.reduce((s, c) => s + (c?.tax_withholding_amount || 0), 0),
      totalNet: data?.commissions?.reduce((s, c) => s + (c?.net_after_tax || c?.commission_amount || 0), 0),
      totalPayments: data?.payments?.reduce((s, p) => s + (p?.amount || 0), 0),
      agentCount: new Set(data.commissions?.filter(c => c.recipient_role === 'agent')?.map(c => c.recipient_id))?.size,
      notaryCount: new Set(data.commissions?.filter(c => c.recipient_role === 'notary')?.map(c => c.recipient_id))?.size,
    };
  };

  const handleDownloadCSV = () => {
    if (!reportData) return;
    const csv = buildSettlementCSV(reportData, filters);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport_reglement_${filters?.dateFrom}_${filters?.dateTo}_${Date.now()}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
    showToast('Rapport CSV téléchargé');
  };

  const handlePrintPDF = () => {
    if (!reportData) return;
    const summary = computeSummary(reportData);
    const html = buildPrintHTML(reportData, filters, summary);
    const win = window.open('', '_blank');
    win?.document?.write(html);
    win?.document?.close();
    win?.focus();
    setTimeout(() => win?.print(), 500);
    showToast('Rapport PDF ouvert pour impression');
  };

  const summary = computeSummary(reportData);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Rapports de Règlement</h2>
          <p className="text-sm text-gray-500 mt-0.5">Transactions, séquestre, frais, paiements nets et retenues fiscales</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-amber-600" />
          <h3 className="text-sm font-semibold text-gray-800">Paramètres du rapport</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Date de début</label>
            <input
              type="date"
              value={filters?.dateFrom}
              onChange={e => setFilters(f => ({ ...f, dateFrom: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Date de fin</label>
            <input
              type="date"
              value={filters?.dateTo}
              onChange={e => setFilters(f => ({ ...f, dateTo: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Rôle destinataire</label>
            <select
              value={filters?.recipientRole}
              onChange={e => setFilters(f => ({ ...f, recipientRole: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            >
              <option value="all">Tous les rôles</option>
              <option value="agent">Agents immobiliers</option>
              <option value="notary">Notaires</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Statut paiement</label>
            <select
              value={filters?.status}
              onChange={e => setFilters(f => ({ ...f, status: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            >
              <option value="all">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="released">Payé</option>
            </select>
          </div>
        </div>
        <div className="mt-4 flex gap-3">
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white rounded-xl text-sm font-semibold transition-colors"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
            Générer le rapport
          </button>
          {reportData && (
            <>
              <button
                onClick={handleDownloadCSV}
                className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-semibold transition-colors"
              >
                <Download className="w-4 h-4" />
                Télécharger CSV
              </button>
              <button
                onClick={handlePrintPDF}
                className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl text-sm font-semibold transition-colors"
              >
                <Printer className="w-4 h-4" />
                Imprimer / PDF
              </button>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {reportData && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Transactions séquestre', value: reportData?.escrow?.length, suffix: 'transactions', color: 'text-blue-600', bg: 'bg-blue-50', icon: '🔒' },
              { label: 'Montant séquestre total', value: formatAmount(summary?.totalEscrow), color: 'text-gray-800', bg: 'bg-gray-50', icon: '💰' },
              { label: 'Commissions brutes', value: formatAmount(summary?.totalCommissions), color: 'text-amber-600', bg: 'bg-amber-50', icon: '📊' },
              { label: 'Retenues fiscales', value: formatAmount(summary?.totalTax), color: 'text-red-600', bg: 'bg-red-50', icon: '🏛️' },
              { label: 'Paiements nets', value: formatAmount(summary?.totalNet), color: 'text-green-600', bg: 'bg-green-50', icon: '✅' },
              { label: 'Paiements reçus', value: formatAmount(summary?.totalPayments), color: 'text-purple-600', bg: 'bg-purple-50', icon: '💳' },
              { label: 'Agents concernés', value: summary?.agentCount, suffix: 'agents', color: 'text-blue-600', bg: 'bg-blue-50', icon: '🏠' },
              { label: 'Notaires concernés', value: summary?.notaryCount, suffix: 'notaires', color: 'text-purple-600', bg: 'bg-purple-50', icon: '⚖️' },
            ]?.map((s, i) => (
              <div key={i} className={`${s?.bg} rounded-xl p-4`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">{s?.icon}</span>
                  <span className="text-xs text-gray-500">{s?.label}</span>
                </div>
                <p className={`text-lg font-bold ${s?.color}`}>{s?.value} {s?.suffix || ''}</p>
              </div>
            ))}
          </div>

          {/* Transactions Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <FileText className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-semibold text-gray-800">Transactions Séquestre ({reportData?.escrow?.length})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Référence', 'Titre', 'Montant', 'Statut', 'Date', 'Libération']?.map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reportData?.escrow?.slice(0, 50)?.map(t => (
                    <tr key={t?.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">{t?.reference || t?.id?.slice(0, 8)}</td>
                      <td className="px-4 py-3 text-gray-800">{t?.item_title || t?.title || '—'}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{formatAmount(t?.amount_total || t?.total_amount)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${t?.status === 'released' ? 'bg-green-50 text-green-700 border-green-200' : t?.status === 'disputed' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                          {t?.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(t?.created_at)}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(t?.released_at)}</td>
                    </tr>
                  ))}
                  {reportData?.escrow?.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Aucune transaction</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Commissions Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-semibold text-gray-800">Commissions & Paiements ({reportData?.commissions?.length})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Destinataire', 'Rôle', 'Commission brute', 'Retenue fiscale', 'Montant net', 'Devise', 'Canal', 'Statut']?.map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reportData?.commissions?.slice(0, 50)?.map(c => (
                    <tr key={c?.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{c?.recipient_name || '—'}</p>
                          <p className="text-xs text-gray-400">{c?.recipient_email || '—'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${c?.recipient_role === 'agent' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                          {c?.recipient_role === 'agent' ? '🏠 Agent' : '⚖️ Notaire'}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-800">{formatAmount(c?.commission_amount)}</td>
                      <td className="px-4 py-3">
                        {c?.tax_withholding_amount ? (
                          <div>
                            <p className="text-red-600 font-medium">{formatAmount(c?.tax_withholding_amount)}</p>
                            <p className="text-xs text-gray-400">{c?.tax_country} {c?.tax_rate_applied ? `— ${(c?.tax_rate_applied * 100)?.toFixed(1)}%` : ''}</p>
                          </div>
                        ) : <span className="text-gray-400 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 font-bold text-green-700">{formatAmount(c?.net_after_tax || c?.commission_amount)}</td>
                      <td className="px-4 py-3 text-xs text-gray-700">
                        {c?.payout_currency && c?.payout_currency !== 'XOF' ? (
                          <div>
                            <p className="font-medium">{c?.payout_currency}</p>
                            {c?.net_payout_amount && <p className="text-xs text-green-600">{formatAmount(c?.net_payout_amount, c?.payout_currency)}</p>}
                          </div>
                        ) : 'XOF'}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">{c?.payout_channel || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${c?.status === 'released' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                          {c?.status === 'released' ? 'Payé' : 'En attente'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {reportData?.commissions?.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Aucune commission</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Empty state */}
      {!reportData && !loading && (
        <div className="bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 p-12 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Configurez les filtres et cliquez sur "Générer le rapport"</p>
          <p className="text-gray-400 text-sm mt-1">Le rapport inclura les transactions, commissions, retenues fiscales et paiements nets</p>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white text-sm font-medium ${toast?.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
          {toast?.type === 'error' ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {toast?.msg}
        </div>
      )}
    </div>
  );
};

export default SettlementReportGenerator;
